import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";
import { allVideos, getCategoryMeta } from "@/lib/videoData";
import type { Video } from "@/types/video";

interface SlideData {
  videoId: string;
  label: string;
  backdrop: string;
  video: Video;
}

const buildSlides = () => {
  const result: SlideData[] = [];
  heroSlides.forEach((slide) => {
    const video = allVideos.find((item) => item.id === slide.videoId);
    if (video) result.push({ ...slide, video });
  });
  return result;
};

const slides = buildSlides();

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (index: number) => {
    setCurrent((index + slides.length) % slides.length);
  };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) goTo(current + (delta < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const active = slides[current];
  if (!active) return null;
  const activeCategory = getCategoryMeta(active.video.category);

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点推荐轮播"
    >
      <div className="relative h-[540px] w-full md:h-[610px]">
        {slides.map((slide, index) => (
          <div
            key={slide.videoId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === current ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={index !== current}
          >
            <img
              src={slide.backdrop}
              alt={`${slide.video.title} 剧照`}
              title={`${slide.video.title} - 糖豆视频精选推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/10 to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1360px] flex-col justify-center px-4 pb-24 md:px-8 md:pb-20">
          <div key={active.videoId} className="animate-fade-up max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-bold text-foreground-950">
                <i className="ri-fire-fill text-xs" />
                {active.label}
              </span>
              <span className="rounded-full border border-background-50/25 px-3 py-1 text-[11px] font-medium text-background-200">
                {activeCategory?.name} · {active.video.year} · {active.video.region}
              </span>
              {active.video.episodeCount > 0 && (
                <span className="rounded-full border border-background-50/25 px-3 py-1 text-[11px] font-medium text-background-200">
                  {active.video.status}
                </span>
              )}
            </div>

            <p className="mt-5 text-xs font-semibold tracking-[0.22em] text-accent-300">
              糖豆视频 · 精选影视综艺高清观看平台
            </p>
            <h1 className="mt-2 font-heading text-4xl font-extrabold leading-tight tracking-tight text-background-50 md:text-[54px]">
              <Link
                to={`/detail/${active.video.id}`}
                className="cursor-pointer transition-colors hover:text-accent-300"
              >
                {active.video.title}
              </Link>
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1 font-semibold text-accent-400">
                <i className="ri-star-fill text-sm" />
                {active.video.rating.toFixed(1)}
                <span className="font-normal text-background-300">/ 10</span>
              </span>
              <span className="text-background-400">|</span>
              <span>{active.video.genre.join(" · ")}</span>
              <span className="text-background-400">|</span>
              <span>{active.video.duration}</span>
            </div>

            <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-background-300 md:line-clamp-4 md:text-[15px]">
              {active.video.description}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.video.id}`}
                className="flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to={`/category/${active.video.category}`}
                className="flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 text-sm font-medium text-background-100 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                更多{activeCategory?.name}
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10">
          <div className="mx-auto flex w-full max-w-[1360px] items-center justify-between gap-4 px-4 pb-6 md:px-8">
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.videoId}
                  type="button"
                  aria-label={`切换到 ${slide.video.title}`}
                  onClick={() => goTo(index)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all ${
                    index === current ? "w-8 bg-accent-500" : "w-4 bg-background-50/35 hover:bg-background-50/60"
                  }`}
                />
              ))}
              <span className="ml-2 font-heading text-xs font-semibold tracking-widest text-background-300">
                0{current + 1} / 0{slides.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => goTo(current - 1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => goTo(current + 1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}